/**
 * The HUD while playing. Top: the Terrella button, with a coloured dot on its edge for every
 * crewmate on the planet (it opens the world sheet), your pockets at the right, and the line about
 * the tile you stand on. Bottom: the buttons of what you do there, and Build. Pinch, scroll or Z
 * zoom out.
 */
import type { JSX } from 'react';

import { openPanel } from '@game/view/panels';
import { t } from '@i18n/index';

import { TileLabel } from '@ui/features/tile/TileLabel';
import { PocketsChip } from '@ui/features/pockets/PocketsChip';
import { useGame, useGameState, useLocale } from '@ui/hooks';
import { Logo } from '@ui/icons';

import { BottomBar } from './BottomBar';

import './hud.css';

/** At most this many dots around the button; the rest are counted. */
const DOTS = 6;
/** Where the dots sit on the button's edge, in degrees clockwise from the top, and how far apart. */
const DOT_START = 30;
const DOT_STEP = 36;
const DOT_RADIUS = 27;

export function Hud(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const world = useGameState((s) => s.world);
  const tiles = useGameState((s) => s.view?.kind.length ?? 0);
  const connection = useGameState((s) => s.connection);
  const onlineMap = useGameState((s) => s.online);
  const members = useGameState((s) => s.members);
  const hubOpen = useGameState((s) => s.panel === 'hub');
  if (!world || !tiles) return null;
  const nameOf = (id: string): string => members.get(id)?.name ?? t('hud.crewmate');
  const colorOf = (id: string): string => members.get(id)?.color ?? '#999999';
  const others = [...onlineMap.keys()];
  const aria =
    others.length === 0
      ? t('hud.chip-alone-aria', { world: world.name })
      : t('hud.chip-aria', {
          world: world.name,
          names: others.map(nameOf).join(', '),
          n: others.length,
        });

  return (
    <>
      <div className="hud top">
        <div className="row">
          <button
            className="world-chip glass"
            type="button"
            aria-haspopup="dialog"
            aria-label={aria}
            title={world.name}
            onClick={() => openPanel(game, 'world')}
          >
            <Logo />
            {others.slice(0, DOTS).map((id, k) => {
              const angle = ((DOT_START + k * DOT_STEP) * Math.PI) / 180;
              return (
                <span
                  key={id}
                  className="mate"
                  data-player={id}
                  title={nameOf(id)}
                  style={{
                    background: colorOf(id),
                    transform: `translate(${(Math.sin(angle) * DOT_RADIUS).toFixed(1)}px, ${(
                      -Math.cos(angle) * DOT_RADIUS
                    ).toFixed(1)}px)`,
                  }}
                />
              );
            })}
            {others.length > DOTS && <span className="more">+{others.length - DOTS}</span>}
            {connection !== 'online' && <span className="dot warn" aria-hidden="true" />}
          </button>
          <PocketsChip />
        </div>
        {!hubOpen && <TileLabel />}
      </div>
      <div className="hud bottom">
        <BottomBar />
      </div>
    </>
  );
}
